import { faCopy, faUsers } from "@fortawesome/pro-regular-svg-icons";
import { Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import { Stack } from "@mui/material";

import { Icon, Modal } from "@/components";

import { useGetInviteCodesQuery } from "@/pages/Private/redux/course-progress/course-progress.api";
import { useCopyToClipboard } from "@/utils/useCopyToClipboard";

import { CourseProgressResponse } from "../schema/course-progress";
import { IConnectedUser } from "../interfaces/IConnectedUser.interface";

export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

interface IAddTeamModalProps {
	isOpen: boolean;
	conntectedDefaultParticipants?: IConnectedUser[];
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	courseProgress: CourseProgressResponse;
}

export default function AddTeamModal({
	isOpen,
	setIsOpen,
	courseProgress,
	conntectedDefaultParticipants,
}: IAddTeamModalProps) {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) => t(`courses.${key}`, options);
	const { handleCopyClick, renderSnackbar } = useCopyToClipboard();
	const { data: codes } = useGetInviteCodesQuery(courseProgress.id, {
		refetchOnMountOrArgChange: true,
	});

	const inviteCode = codes?.generalCode ?? courseProgress.code;

	// Build a socket status lookup map (for joined/pending/left)
	const socketStatusMap = new Map<string, IConnectedUser>();

	if (conntectedDefaultParticipants) {
		for (const u of conntectedDefaultParticipants) {
			socketStatusMap.set(u.id, u);
		}
	}

	// The PINs come from the API (only the leader gets them), enriched with live status from
	// the socket. Until they have loaded, the socket list is shown without PINs.
	const participantsToDisplay = codes?.defaultParticipants
		? codes.defaultParticipants.map((participant) => {
				const socketUser = socketStatusMap.get(participant.id);

				return {
					...participant,
					isActive: socketUser?.isActive ?? false,
					firstJoinTimeStamp: socketUser?.firstJoinTimeStamp,
				};
			})
		: (conntectedDefaultParticipants ?? []).map((user) => ({
				...user,
				otpCode: undefined as string | undefined,
			}));

	return (
		<Modal
			aboveHeader={
				<div
					className="flex justify-center items-center rounded-xl border border-blue-100 text-gray-700 cursor-pointer pointer-events-auto"
					style={{ width: "48px", height: "48px" }}
				>
					<Icon className="w-[24px] text-primary-gray h-[24px]" icon={faUsers} />
				</div>
			}
			dialogContentClassName="!p-0"
			handleClose={() => setIsOpen(false)}
			isLoading={false}
			isOpened={isOpen}
			size="xs"
			title={""}
		>
			<Stack className="mt-4">
				<div className="flex-grow w-full mb-4">
					<h3 className="mb-1 text-primary-blue text-[20px] font-semibold">
						{ts("progress.add-team")}
					</h3>
					{participantsToDisplay.length > 0 ? (
						<p className={`text-gray-700 lg:max-w-[650px] text-[14px] pr-5 `}>
							{ts("start.invite-colleagues-description", {
								url: window.location.origin + "/first-login",
								interpolation: { escapeValue: false },
							})}
						</p>
					) : null}
					<div
						className={`flex gap-5 flex-col w-full  ${participantsToDisplay.length > 0 ? "mt-5" : ""}`}
					>
						{participantsToDisplay.map((user) => (
							<div key={user.id} className="flex flex-row gap-2 items-center justify-center ">
								<div className="flex flex-row gap-2 items-center justify-center w-fit">
									<div
										className="flex flex-row gap-2 items-center cursor-pointer justify-center text-gray-700 lg:max-w-[650px] text-[14px] text-sm font-normal bg-gray-100 p-2 rounded-md w-fit border border-gray-200"
										onClick={() =>
											handleCopyClick(
												(
													ts("inviteText", {
														code: user.otpCode,
														link: window.location.origin + "/first-login",
													}) ?? ""
												).replace(/\\n/g, "\n")
											)
										}
									>
										<Icon
											className="w-[20px] text-gray-700 h-[20px] cursor-pointer font-normal"
											icon={faCopy}
										/>
									</div>
								</div>
								<div className="flex flex-row gap-2  w-[252px] justify-between">
									<p className="text-sm font-normal flex items-center justify-center text-primary-blue-dark">
										{user.firstName} {user.lastName}
									</p>
									{user.isActive ? (
										<span
											className={`bg-success-light mr-2 capitalize py-1 px-2 rounded-md text-text text-[12px] text-nowrap font-medium `}
										>
											{ts("joined")}
										</span>
									) : !user.firstJoinTimeStamp && !user.isActive ? (
										<span
											className={`bg-warning-light mr-2 capitalize py-1 px-2 rounded-md text-warning-ink text-[12px] text-nowrap font-medium `}
										>
											{ts("pending")}
										</span>
									) : user.firstJoinTimeStamp && !user.isActive ? (
										<span
											className={`bg-error-light mr-2 capitalize py-1 px-2 rounded-md text-text text-[12px] text-nowrap font-medium `}
										>
											{ts("left")}
										</span>
									) : null}
								</div>
							</div>
						))}
					</div>
					<div className="mt-5 ">
						<p className="text-gray-700 lg:max-w-[650px] text-[14px] pr-5 mb-5">
							{ts("externalInviteInfo")}
						</p>

						<div className="flex flex-row gap-2 items-center justify-center w-full">
							<div className="flex flex-row gap-2 items-center justify-center w-fit">
								<div
									className="flex flex-row gap-2 items-center cursor-pointer justify-center text-gray-700 lg:max-w-[650px] text-[14px] text-sm font-normal bg-gray-100 p-2 rounded-md w-fit border border-gray-200"
									onClick={() => {
										handleCopyClick(
											(
												ts("inviteText", {
													code: inviteCode,
													link: window.location.origin + "/first-login",
												}) ?? ""
											).replace(/\\n/g, "\n")
										);
									}}
								>
									<Icon
										className="w-[20px] text-gray-700 h-[20px] cursor-pointer font-normal"
										icon={faCopy}
									/>
								</div>
							</div>
							<div className="flex flex-row gap-2  w-[252px] justify-between">
								<p className="text-sm font-normal flex items-center justify-center text-primary-blue-dark">
									{ts("generalInvite")}
								</p>
							</div>
						</div>
					</div>
				</div>
				{renderSnackbar()}
			</Stack>
		</Modal>
	);
}
